<template>
  <div id="mdsList">
    <div class="atb-flex atb-flex-row atb-gap-4 atb-pt-6 atb-px-4 atb-pb-4">
      <div class="atb-flex-grow">
        <div class="eds-table-wrap atb-w-full atb-h-full">
          <Toolbar
            class-name="atb-bg-white atb-border-t-0 atb-border-x-0 atb-border-b atb-border-solid atb-border-gray-300"
          >
            <template #toolbarSection>
              <ToolbarSection>
                <template #sectionContent>
                  <Button
                    id="mdsListRefreshButton"
                    motif="tertiary"
                    :title="$t('mdsRefresh')"
                    aria-label="refresh button"
                    :disabled="refreshing"
                    @click="refresh"
                  >
                    <template #icon>
                      <span class="material-icons">loop</span>
                    </template>
                  </Button>
                </template>
              </ToolbarSection>
              <ToolbarSection v-if="canModify">
                <template #sectionContent>
                  <Button
                    id="mdsListCreateButton"
                    motif="tertiary"
                    :title="$t('mdsCreate')"
                    aria-label="create button"
                    @click="emit('create')"
                  >
                    <template #icon>
                      <span class="material-icons">add_circle_outline</span>
                    </template>
                  </Button>
                </template>
              </ToolbarSection>
              <ToolbarSection v-if="canModify">
                <template #sectionContent>
                  <Button
                    id="mdsListCopyButton"
                    motif="tertiary"
                    :title="$t('mdsCopy')"
                    aria-label="copy button"
                    @click="copySelected"
                  >
                    <template #icon>
                      <span class="material-icons">content_copy</span>
                    </template>
                  </Button>
                </template>
              </ToolbarSection>
              <ToolbarSection v-if="canModify">
                <template #sectionContent>
                  <Button
                    id="mdsListAttachButton"
                    motif="tertiary"
                    :title="$t('mdsAttach')"
                    aria-label="attach button"
                    @click="showAttach = true"
                  >
                    <template #icon>
                      <span class="material-icons">link</span>
                    </template>
                  </Button>
                </template>
              </ToolbarSection>
            </template>
          </Toolbar>
          <AgGridVue
            id="mdsListGrid"
            :style="{ height: '500px' }"
            class="ag-theme-eds atb-w-full atb-h-full"
            :column-defs="columnDefs"
            :row-data="rowData"
            :components="gridComponents"
            :overlay-no-rows-template="noRowsTemplate"
            :row-selection="'single'"
            :suppress-row-click-selection="true"
            :suppress-cell-selection="true"
            @grid-ready="onGridReady"
          />
        </div>
      </div>
    </div>
    <MdsAttachModal
      :show="showAttach"
      @close="showAttach = false"
    />
  </div>
</template>

<script setup lang="ts">
  import { computed, Ref, ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { AgGridVue } from 'ag-grid-vue3/lib/AgGridVue';
  import { GridApi, GridReadyEvent } from 'ag-grid-community';
  import Button from '@atb-ui/eds-vue/src/components/Button.vue';
  import Toolbar from '@atb-ui/eds-vue/src/components/Toolbar/Toolbar.vue';
  import ToolbarSection from '@atb-ui/eds-vue/src/components/Toolbar/ToolbarSection.vue';
  import ClickableCell from '@/app/shared/components/grid-cells/ClickableCell.vue';
  import { lastUpdatedRenderer } from '@/app/shared/components/grid-cells/cellRenderers';
  import { useClientStore } from '@/app/features/ams/client/client-store';
  import { Datasource } from '@/app/shared/types/datasource.interface';
  import { refreshSelectedClient } from '@/app/features/ams/tools/mds/multi-datasource-service';
  import { useAlertStore } from '@/app/shared/stores/alert-store';
  import { Motif } from '@/app/shared/enums/motif-enum';
  import MdsAttachModal from '@/app/features/ams/tools/mds/components/MdsAttachModal.vue';

  /**
   * "All Multi Data Sources" tab: the selected client's multi datasources, as in 5.x
   * (Long Name opens the datasource in its own tab, Short Name, Last Updated).
   */
  interface MdsListEvents {
    (_event: 'create'): void;
    (_event: 'copy', _datasource: Datasource): void;
    (_event: 'edit', _datasource: Datasource): void;
  }
  interface MdsListProps {
    canModify: boolean;
  }

  defineProps<MdsListProps>();
  const emit = defineEmits<MdsListEvents>();
  const { t } = useI18n();
  const clientStore = useClientStore();
  const gridApi = ref(null) as Ref<GridApi | null>;
  const refreshing = ref(false);
  const showAttach = ref(false);
  const alertStore = useAlertStore();

  const rowData = computed<Array<Datasource>>(() =>
    (clientStore.selectedClient?.dataSources || [])
      .filter((ds: Datasource) => ds.isMulti)
      .sort((a: Datasource, b: Datasource) => a.name.localeCompare(b.name))
  );

  const openDatasource = (id: number) => {
    const datasource = rowData.value.find((ds) => ds.id === id);
    if (datasource) {
      emit('edit', datasource);
    }
  };

  const columnDefs = [
    {
      field: 'check',
      headerName: '',
      width: 56,
      suppressSizeToFit: true,
      checkboxSelection: true,
    },
    {
      field: 'name',
      headerName: t('mdsColumnLongName'),
      suppressMovable: true,
      minWidth: 200,
      sortable: true,
      cellRendererFramework: ClickableCell,
      cellRendererParams: {
        onClick: openDatasource,
      },
    },
    {
      field: 'acronym',
      headerName: t('mdsColumnShortName'),
      suppressMovable: true,
      minWidth: 150,
      sortable: true,
    },
    {
      field: 'updatedAt',
      headerName: t('mdsColumnUpdated'),
      suppressMovable: true,
      minWidth: 200,
      sortable: true,
      cellRenderer: 'lastUpdatedRenderer',
    },
  ];

  const gridComponents = {
    lastUpdatedRenderer,
  };

  const noRowsTemplate = `<span>${t('mdsNoMultiDatasources')}</span>`;

  const onGridReady = (params: GridReadyEvent) => {
    gridApi.value = params.api;
    gridApi.value.sizeColumnsToFit();
  };

  // 5.x "Copy existing multi data source": duplicate the ticked one into a new tab.
  const copySelected = () => {
    const selected: Array<Datasource> = gridApi.value?.getSelectedRows() || [];
    if (selected.length !== 1) {
      alertStore.showNotification(
        t('mdsCopySelectOne'),
        t('mdsPage'),
        Motif.ERROR
      );
      return;
    }
    emit('copy', selected[0]);
  };

  const refresh = async () => {
    refreshing.value = true;
    try {
      await refreshSelectedClient();
    } finally {
      refreshing.value = false;
    }
  };
</script>
